import {Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {Button} from 'primeng/button';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  CheckPreviousPhaseFinishedRequestParams,
  CompleteRfiEvaluationSubcriteriaStepRequestParams,
  EvaluationCriteriaControllerService,
  EvaluationCriteriaResponse,
  EvaluationSubCriteriaView,
  GetEvaluationCriteriaByProjectRequestParams,
  ParticipatingToolControllerService,
  ProjectRoleType,
  ProjectStepType,
  TenderPhaseType,
} from '@frontend/shared-api';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {SubCriteriaTabsComponent} from './components/subcriteria-tabs/subcriteria-tabs.component';
import {SavedSubCriteriaAttributes} from './components/subcriteria-tabs/subcriteria-tabs.types';
import {SubCriteriaAttributeEvaluationService} from '../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.service';
import {firstValueFrom, map, NEVER, Observable, of} from 'rxjs';
import {AttributeTransformationService} from '../../../../../../shared/services/attribute-transformation.service';
import {SubCriteriaConfig} from '../../../../../../shared/model/sub-criteria.interfaces';
import {AttributesByEvaluationCriteriaMap} from '../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {ProgressDashboardComponent} from '../../../../../../shared/components/progress-dashboard/progress-dashboard.component';
import {
  DirectusService,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
} from '@frontend/directus';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {ContributorMenuBarComponent} from '../../../../../../shared/components/contributor-menu-bar/contributor-menu-bar.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {ProjectUserDetailService} from '../../../../../../shared/services/project-user-detail.service';
import {RoleCheckService} from '../../../../../../shared/services/role-check.service';
import {
  AVERAGE_USER_EMAIL,
  AVERAGED_SCORE_AUTHOR,
} from '../../../../../../shared/constants/contributor.constants';
import {
  EVALUATION_CRITERIA,
  isEC1,
} from '../../../../../../shared/constants/evaluation-criteria.constants';
import {ConfirmationDialogService} from '../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

const COLLECTION_POSTFIX = 'rfi';

@Component({
  imports: [
    Button,
    TranslocoPipe,
    SubCriteriaTabsComponent,
    ProgressDashboardComponent,
    ContributorMenuBarComponent,
    LoadingComponent,
  ],
  templateUrl: './step-5-1-rfi-subcriteria-evaluation.component.html',
})
export class Step51RfiSubCriteriaEvaluationComponent extends ProjectStepBaseReactive {
  protected readonly ProjectStepType = ProjectStepType;
  protected readonly ProjectRoleType = ProjectRoleType;

  private readonly evaluationCriteriaController = inject(EvaluationCriteriaControllerService);
  private readonly subCriteriaService = inject(SubCriteriaAttributeEvaluationService);
  private readonly attributeTransformationService = inject(AttributeTransformationService);
  private readonly directusService = inject(DirectusService);
  private readonly participatingToolController = inject(ParticipatingToolControllerService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly projectUserDetail = inject(ProjectUserDetailService).createUserDetailSignal(
    () => this.projectId(),
    inject(DestroyRef),
  );

  protected readonly selectedContributorEmail = signal<string | undefined>(undefined);
  protected readonly hasUnsavedChanges = signal(false);
  protected readonly isFinishingStep = signal(false);

  private readonly criteriaResource = rxResource({
    params: () => this.criteriaRequest(),
    stream: ({params: request}) =>
      request === undefined
        ? NEVER
        : this.loadEc1Criteria(request.projectId, request.contributorEmail),
  });

  private readonly attributesResource = rxResource({
    params: () => this.attributesRequest(),
    stream: ({params: request}) =>
      request === undefined
        ? NEVER
        : this.loadEc1Attributes(request.subCriteriaConfigs, request.scoreAuthor),
    defaultValue: {},
  });

  protected readonly isProjectManager = computed<boolean>(() =>
    (this.projectUserDetail()?.roles ?? []).includes(ProjectRoleType.PROJECT_MANAGER),
  );

  private readonly previousPhaseResource = rxResource({
    params: () => (this.isProjectManager() ? this.projectId() : undefined),
    stream: ({params: request}) =>
      request === undefined ? NEVER : this.loadPreviousPhaseFinished(request),
    defaultValue: false,
  });

  private readonly directusCriteria = toSignal(this.directusService.loadEvaluationCriteria(), {
    initialValue: [] as EvaluationCriteria[],
  });

  private readonly directusSubCriteria = toSignal(this.directusService.loadSubCriteria(), {
    initialValue: [] as EvaluationSubCriteriaDirectus[],
  });

  protected readonly loading = computed<boolean>(
    () => this.criteriaResource.isLoading() || this.attributesResource.isLoading(),
  );

  protected readonly projectCriteria = computed<EvaluationCriteriaResponse[]>(() => {
    const ec1Criteria = this.criteriaResource.value();

    return ec1Criteria ? [ec1Criteria] : [];
  });

  protected readonly dashboardDirectusCriteria = computed<EvaluationCriteria[]>(() => {
    const directusIds = this.projectCriteria().map((ec1Criteria) => ec1Criteria.directusId);

    return this.directusCriteria().filter((criterion) => directusIds.includes(criterion.id));
  });

  protected readonly dashboardDirectusSubCriteria = computed<EvaluationSubCriteriaDirectus[]>(
    () => {
      const scNumbers = this.projectCriteria()
        .flatMap((ec1Criteria) => ec1Criteria.subCriteria)
        .map((sc) => sc.scNumber);

      return this.directusSubCriteria().filter((sc) => scNumbers.includes(sc.sc_number));
    },
  );

  protected readonly attributesByEvaluationCriteria = computed<AttributesByEvaluationCriteriaMap>(
    () => this.attributesResource.value(),
  );

  protected readonly contributorEmail = computed<string | undefined>(
    () => this.selectedContributorEmail() ?? this.currentUserEmail(),
  );

  protected readonly isScoringDisabled = computed<boolean>(() => {
    const selected = this.selectedContributorEmail();
    const current = this.currentUserEmail();

    if (this.isButtonDisabled() || selected === AVERAGE_USER_EMAIL) {
      return true;
    }

    return selected !== undefined && current !== undefined && selected !== current;
  });

  protected readonly hasCriteria = computed<boolean>(() => this.projectCriteria().length > 0);

  protected readonly previousPhaseFinished = computed<boolean>(() =>
    this.previousPhaseResource.value(),
  );

  protected readonly allAttributesScoredByAnyone = computed<boolean>(() => {
    const attributesByCriteria = this.attributesByEvaluationCriteria();

    return this.projectCriteria().every((ec1Criteria) =>
      ec1Criteria.subCriteria
        .filter((sc) => sc.included)
        .every((sc) => {
          const scoredDirectusIds = this.scoredDirectusIds(sc);

          return (attributesByCriteria[ec1Criteria.id]?.[sc.id] ?? []).every((attribute) =>
            scoredDirectusIds.has(attribute.directusId),
          );
        }),
    );
  });

  protected readonly canSeeAverages = computed<boolean>(
    () => this.isProjectManager() || this.roleCheckService.hasCustomerAdminRole(),
  );

  protected readonly showConsistency = computed<boolean>(
    () => this.contributorEmail() === AVERAGE_USER_EMAIL && this.canSeeAverages(),
  );

  protected readonly isAverageOrOwnSelection = computed<boolean>(() => {
    const contributorEmail = this.contributorEmail();

    return contributorEmail === AVERAGE_USER_EMAIL || contributorEmail === this.currentUserEmail();
  });

  protected readonly canFinishStep = computed<boolean>(
    () =>
      this.isProjectManager() &&
      this.isAverageOrOwnSelection() &&
      this.previousPhaseFinished() &&
      !this.isButtonDisabled() &&
      this.hasCriteria() &&
      this.allAttributesScoredByAnyone() &&
      !this.hasUnsavedChanges(),
  );

  private readonly subCriteriaConfigs = computed<SubCriteriaConfig[]>(() =>
    this.projectCriteria().flatMap((ec1Criteria) =>
      ec1Criteria.subCriteria
        .filter((sc) => sc.included)
        .map((sc) => ({
          evaluationCriteriaId: ec1Criteria.id,
          ecNumber: ec1Criteria.ecNumber,
          subCriteriaId: sc.id,
          evaluationCriteriaNumber: EVALUATION_CRITERIA.EC1,
          subCriteriaData: sc,
        })),
    ),
  );

  onContributorSelected(contributorEmail: string): void {
    this.selectedContributorEmail.set(contributorEmail);
    this.hasUnsavedChanges.set(false);
  }

  onUnsavedScoresChanged(hasUnsavedScores: boolean): void {
    this.hasUnsavedChanges.set(hasUnsavedScores);
  }

  finishStep(): void {
    this.confirmDialogService.confirm({
      headerKey: 'project.finish.step.5-1.confirm.title',
      messageKey: 'project.finish.step.5-1.confirm.description',
      accept: () => void this.completeStep(),
    });
  }

  private async completeStep(): Promise<void> {
    this.isFinishingStep.set(true);

    try {
      const request: CompleteRfiEvaluationSubcriteriaStepRequestParams = {
        projectId: this.projectId()!,
      };
      await firstValueFrom(
        this.evaluationCriteriaController.completeRfiEvaluationSubcriteriaStep(request),
      );
      await this.stepState.loadStepStatuses(this.projectId()!);
    } finally {
      this.isFinishingStep.set(false);
    }
  }

  private criteriaRequest(): {projectId: number; contributorEmail: string} | undefined {
    const projectId = this.projectId();
    const contributorEmail = this.contributorEmail();

    if (projectId === undefined || contributorEmail === undefined) {
      return undefined;
    }

    return {projectId: projectId, contributorEmail: contributorEmail};
  }

  private attributesRequest():
    {subCriteriaConfigs: SubCriteriaConfig[]; scoreAuthor: string} | undefined {
    const scoreAuthor = this.scoreAuthor();
    const subCriteriaConfigs = this.subCriteriaConfigs();

    if (
      scoreAuthor === undefined ||
      subCriteriaConfigs.length === 0 ||
      this.criteriaResource.isLoading()
    ) {
      return undefined;
    }

    return {subCriteriaConfigs: subCriteriaConfigs, scoreAuthor: scoreAuthor};
  }

  private scoreAuthor(): string | undefined {
    const contributorEmail = this.contributorEmail();

    return contributorEmail === AVERAGE_USER_EMAIL ? AVERAGED_SCORE_AUTHOR : contributorEmail;
  }

  private currentUserEmail(): string | undefined {
    return this.projectUserDetail()?.email;
  }

  private scoredDirectusIds(subCriteria: EvaluationSubCriteriaView): Set<string> {
    return new Set(
      subCriteria.attributes
        .filter((attribute) => attribute.importanceScores.length > 0)
        .map((attribute) => attribute.directusId),
    );
  }

  private loadPreviousPhaseFinished(projectId: number): Observable<boolean> {
    const request: CheckPreviousPhaseFinishedRequestParams = {
      projectId: projectId,
      phase: TenderPhaseType.RFI,
    };

    return this.participatingToolController
      .checkPreviousPhaseFinished(request)
      .pipe(map((response) => response.isPreviousPhaseFinished));
  }

  private loadEc1Criteria(
    projectId: number,
    contributorEmail: string,
  ): Observable<EvaluationCriteriaResponse | undefined> {
    const request: GetEvaluationCriteriaByProjectRequestParams =
      contributorEmail === AVERAGE_USER_EMAIL
        ? {projectId: projectId, averageScores: true}
        : {projectId: projectId};

    return this.evaluationCriteriaController
      .getEvaluationCriteriaByProject(request)
      .pipe(map((criteria) => criteria.find((criterion) => isEC1(criterion.ecNumber))));
  }

  private loadEc1Attributes(
    subCriteriaConfigs: SubCriteriaConfig[],
    scoreAuthor: string,
  ): Observable<AttributesByEvaluationCriteriaMap> {
    if (subCriteriaConfigs.length === 0) {
      return of({});
    }

    return this.subCriteriaService
      .loadBatchSubCriteriaAttributesForEvaluation(
        this.projectId()!,
        subCriteriaConfigs,
        COLLECTION_POSTFIX,
        scoreAuthor,
      )
      .pipe(
        map((batchResult) =>
          this.attributeTransformationService.transformBatchAttributeResult(
            batchResult,
            subCriteriaConfigs,
          ),
        ),
      );
  }

  protected async onSaveSuccess(saved: SavedSubCriteriaAttributes): Promise<void> {
    this.applySavedAttributes(saved);

    await this.stepState.loadStepStatuses(this.projectId()!);
  }

  protected onAttributesChanged(changed: SavedSubCriteriaAttributes): void {
    this.applySavedAttributes(changed);
  }

  private applySavedAttributes(saved: SavedSubCriteriaAttributes): void {
    this.attributesResource.update((attributesByCriteria) => ({
      ...attributesByCriteria,
      [saved.evaluationCriteriaId]: {
        ...attributesByCriteria[saved.evaluationCriteriaId],
        [saved.subCriteriaId]: saved.attributes,
      },
    }));
  }
}
